/**
 * ZITELOU - Users Page
 * Liste et gestion des utilisateurs
 */

import React, { useState, useEffect, useCallback } from 'react';

import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  Input,
  Select,
  Button,
  Table,
  Pagination,
  Badge,
  Loader,
  ConfirmModal,
    Modal,
} from '../components/Common';
import { useApi } from '../hooks';
import api from '../services/api';
import { formatDate } from '../utils';
import { toast } from 'react-toastify';

const Users = () => {
  const { loading, execute } = useApi();
  const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 25,
        total: 0,
        totalPages: 0,
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [createUserData, setCreateUserData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        sendOtpEmail: true,
        phoneNumber: '',
        shippingAddress: '',
        shippingCity: '',
        shippingZipCode: '',
        createSubscription: true,
        planId: null,
        billingCycle: 'monthly',
    });
    const [createUserErrors, setCreateUserErrors] = useState({});
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);

  // Filters
    const fetchUsers = useCallback(async () => {
        await execute(async () => {
            const result = await api.getUsers({
                page: pagination.currentPage,
                limit: pagination.pageSize,
                search: searchTerm,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                sortBy: sortBy,
                sortOrder: sortOrder,
            });
            setUsers(result.data);
            setPagination(prev => ({
                ...prev,
                total: result.pagination.total,
                totalPages: result.pagination.totalPages,
            }));
        });
    }, [execute, pagination.currentPage, pagination.pageSize, searchTerm, statusFilter, sortBy, sortOrder]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Charger les plans d'abonnement au montage
    useEffect(() => {
        const loadPlans = async () => {
            const plans = await api.getSubscriptionPlans();
            setSubscriptionPlans(plans);
        };
        loadPlans();
    }, []);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, currentPage: newPage }));
    };

    const handlePageSizeChange = (newSize) => {
        setPagination(prev => ({
            ...prev,
            pageSize: newSize,
            currentPage: 1 // Reset to first page
        }));
    };

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    };

    const handleSort = (columnKey, order) => {
        // Mapper les clés de colonnes vers les champs API
        const columnToApiField = {
            'id': 'id',
            'name': 'firstName', // On trie par prénom pour le nom complet
            'created_at': 'createdAt',
        };
        const apiField = columnToApiField[columnKey] || columnKey;
        setSortBy(apiField);
        setSortOrder(order);
        setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    };

  const handleDelete = async () => {
    try {
      await api.deleteUser(userToDelete.id);
      toast.success('Utilisateur supprimé avec succès');
      setUsers(users.filter((u) => u.id !== userToDelete.id));
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

    const validateCreateUser = () => {
        const errors = {};

        if (!createUserData.firstname.trim()) {
            errors.firstname = 'Le prénom est requis';
        }

        if (!createUserData.lastname.trim()) {
            errors.lastname = 'Le nom est requis';
        }

        if (!createUserData.email.trim()) {
            errors.email = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createUserData.email)) {
            errors.email = 'L\'email n\'est pas valide';
        }

        // Le mot de passe est optionnel, sera généré automatiquement si non fourni
        if (createUserData.password && createUserData.password.length < 6) {
            errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
        }

        setCreateUserErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateUser = async () => {
        if (!validateCreateUser()) {
            return;
        }

        try {
            const result = await api.createUser(createUserData);
            let successMessage = 'Utilisateur créé avec succès';
            if (result.subscriptionCreated) {
                successMessage += ' avec abonnement actif';
            }
            if (result.otpEmailSent) {
                successMessage += '. Email avec mot de passe temporaire envoyé.';
            } else if (result.generatedPassword) {
                successMessage += `. Mot de passe généré : ${result.generatedPassword}`;
            }
            toast.success(successMessage);
            setShowCreateModal(false);
            setCreateUserData({
                firstname: '',
                lastname: '',
                email: '',
                password: '',
                sendOtpEmail: true,
                phoneNumber: '',
                shippingAddress: '',
                shippingCity: '',
                shippingZipCode: '',
                createSubscription: true,
                planId: null,
                billingCycle: 'monthly',
            });
            setCreateUserErrors({});
            fetchUsers();
        } catch (error) {
            toast.error(error.message || 'Erreur lors de la création de l\'utilisateur');
        }
    };

    const handleCreateModalClose = () => {
        setShowCreateModal(false);
        setCreateUserData({
            firstname: '',
            lastname: '',
            email: '',
            password: '',
            createSubscription: true,
            planId: null,
            billingCycle: 'monthly',
        });
        setCreateUserErrors({});
    };

  // Table columns
  const columns = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (value) => <span className="text-gray-500">#{value}</span>,
    },
    {
      key: 'name',
      label: 'Nom & Prénom',
      sortable: true,
      render: (_, row) => (
        <div>
          <p className="font-medium text-gray-900">
            {row.firstname} {row.lastname}
          </p>
          <p className="text-sm text-gray-500">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'created_at',
      label: 'Inscription',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: 'subscription_status',
      label: 'Statut',
      render: (value) => <Badge status={value} dot />,
    },
    {
      key: 'children',
      label: 'Enfants',
      render: (children) => (
        <span className="text-sm text-gray-600">{children.length}</span>
      ),
    },
    {
      key: 'shippingCity',
      label: 'Ville',
      render: (value, row) => (
        <div className="text-sm">
          {value ? (
            <span className="text-gray-900">{row.shippingZipCode} {value}</span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
              <div className="flex items-center gap-2">
                  <Link to={`/users/${row.id}`}>
                      <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                          <EyeIcon className="w-4 h-4 text-gray-600"/>
                      </button>
                  </Link>
                  <Link to={`/users/${row.id}/edit`}>
                      <button
                          className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
                      >
                          <PencilIcon className="w-4 h-4 text-orange-600"/>
                      </button>
                  </Link>
                  <button
                      onClick={() => {
                          setUserToDelete(row);
                          setShowDeleteModal(true);
                      }}
                      className="p-2 hover:bg-danger/10 rounded-lg transition-colors"
                  >
                      <TrashIcon className="w-4 h-4 text-danger"/>
                  </button>
              </div>
          ),
    },
  ];

  if (loading && users.length === 0) {
    return <Loader fullScreen text="Chargement des utilisateurs..." />;
  }

    const startIndex = (pagination.currentPage - 1) * pagination.pageSize + 1;
    const endIndex = Math.min(pagination.currentPage * pagination.pageSize, pagination.total);



    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Utilisateurs</h1>
                    <p className="text-gray-600 mt-2">
                        Gérer les utilisateurs de la plateforme Zitelou
                    </p>
                </div>
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    + Ajouter un utilisateur
                </Button>
            </div>

            {/* Filters Card */}
            <Card>
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <Input
                                placeholder="Rechercher par nom, email..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                                rightIcon={
                                    searchTerm && (
                                        <button onClick={() => handleSearchChange('')}>
                                            <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                                        </button>
                                    )
                                }
                            />
                        </div>
                        <Button
                            variant={showFilters ? 'primary' : 'outline'}
                            onClick={() => setShowFilters(!showFilters)}
                            leftIcon={<FunnelIcon className="w-5 h-5" />}
                        >
                            Filtres
                        </Button>
                    </div>

                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                            <Select
                                label="Statut"
                                value={statusFilter}
                                onChange={(e) => handleStatusFilterChange(e.target.value)}
                                options={[
                                    { value: 'all', label: 'Tous' },
                                    { value: 'active', label: 'Actif' },
                                    { value: 'expired', label: 'Expiré' },
                                    { value: 'suspended', label: 'Suspendu' },
                                    { value: 'cancelled', label: 'Résilié' },
                                ]}
                            />
                        </div>
                    )}
                </div>
            </Card>

            {/* Results Info */}
            <div className="flex items-center justify-between text-sm text-gray-600">
                <p>
                    Affichage de {startIndex} à {endIndex} sur {pagination.total} utilisateurs
                </p>
                <Select
                    value={pagination.pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    options={[
                        { value: 10, label: '10 par page' },
                        { value: 25, label: '25 par page' },
                        { value: 50, label: '50 par page' },
                        { value: 100, label: '100 par page' },
                    ]}
                />
            </div>

            {/* Table */}
            <Card padding="none">
                <Table
                    columns={columns}
                    data={users}
                    keyField="id"
                    loading={loading}
                    emptyMessage="Aucun utilisateur trouvé"
                    onSort={handleSort}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                />
            </Card>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="flex justify-center">
                    <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

        {/* Create User Modal */}
        <Modal
            isOpen={showCreateModal}
            onClose={handleCreateModalClose}
            title="Créer un nouvel utilisateur"
        >
            <div className="space-y-4">
                <Input
                    label="Prénom"
                    type="text"
                    value={createUserData.firstname}
                    onChange={(e) => setCreateUserData({ ...createUserData, firstname: e.target.value })}
                    error={createUserErrors.firstname}
                    placeholder="Entrez le prénom"
                />

                <Input
                    label="Nom"
                    type="text"
                    value={createUserData.lastname}
                    onChange={(e) => setCreateUserData({ ...createUserData, lastname: e.target.value })}
                    error={createUserErrors.lastname}
                    placeholder="Entrez le nom"
                />

                <Input
                    label="Email"
                    type="email"
                    value={createUserData.email}
                    onChange={(e) => setCreateUserData({ ...createUserData, email: e.target.value })}
                    error={createUserErrors.email}
                    placeholder="exemple@email.com"
                />

                <Input
                    label="Mot de passe"
                    type="password"
                    value={createUserData.password}
                    onChange={(e) => setCreateUserData({ ...createUserData, password: e.target.value })}
                    error={createUserErrors.password}
                    placeholder="Au moins 6 caractères (optionnel, sera généré si vide)"
                    disabled={createUserData.sendOtpEmail}
                />

                {/* Option envoi OTP par email */}
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <input
                        type="checkbox"
                        id="sendOtpEmail"
                        checked={createUserData.sendOtpEmail}
                        onChange={(e) => setCreateUserData({ 
                            ...createUserData, 
                            sendOtpEmail: e.target.checked,
                            password: e.target.checked ? '' : createUserData.password 
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div>
                        <label htmlFor="sendOtpEmail" className="text-sm font-medium text-blue-800">
                            Envoyer un mot de passe temporaire par email
                        </label>
                        <p className="text-xs text-blue-600">
                            L'utilisateur recevra un email avec un mot de passe temporaire qu'il devra changer à sa première connexion.
                        </p>
                    </div>
                </div>

                {/* Section Coordonnées */}
                <div className="pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-700 mb-3">Coordonnées</h4>
                    
                    <div className="space-y-4">
                        <Input
                            label="Téléphone"
                            type="tel"
                            value={createUserData.phoneNumber}
                            onChange={(e) => setCreateUserData({ ...createUserData, phoneNumber: e.target.value })}
                            placeholder="06 12 34 56 78"
                        />
                    </div>
                </div>

                {/* Section Adresse de livraison */}
                <div className="pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-700 mb-3">Adresse de livraison</h4>
                    
                    <div className="space-y-4">
                        <Input
                            label="Adresse"
                            type="text"
                            value={createUserData.shippingAddress}
                            onChange={(e) => setCreateUserData({ ...createUserData, shippingAddress: e.target.value })}
                            placeholder="123 rue de la Paix"
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Code postal"
                                type="text"
                                value={createUserData.shippingZipCode}
                                onChange={(e) => setCreateUserData({ ...createUserData, shippingZipCode: e.target.value })}
                                placeholder="75000"
                            />
                            <Input
                                label="Ville"
                                type="text"
                                value={createUserData.shippingCity}
                                onChange={(e) => setCreateUserData({ ...createUserData, shippingCity: e.target.value })}
                                placeholder="Paris"
                            />
                        </div>
                    </div>
                </div>

                {/* Section Abonnement */}
                <div className="pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-700 mb-3">Abonnement</h4>
                    
                    <div className="flex items-center gap-3 mb-4">
                        <input
                            type="checkbox"
                            id="createSubscription"
                            checked={createUserData.createSubscription}
                            onChange={(e) => setCreateUserData({ ...createUserData, createSubscription: e.target.checked })}
                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                        <label htmlFor="createSubscription" className="text-sm font-medium text-gray-700">
                            Créer un abonnement actif
                        </label>
                    </div>

                    {createUserData.createSubscription && (
                        <div className="space-y-4 pl-7">
                            <Select
                                label="Plan d'abonnement"
                                value={createUserData.planId || ''}
                                onChange={(e) => setCreateUserData({ ...createUserData, planId: e.target.value ? parseInt(e.target.value) : null })}
                                options={[
                                    { value: '', label: 'Aucun plan spécifique' },
                                    ...subscriptionPlans.map(plan => ({
                                        value: plan.id.toString(),
                                        label: `${plan.name} (${plan.type || 'standard'})`
                                    }))
                                ]}
                            />

                            <Select
                                label="Cycle de facturation"
                                value={createUserData.billingCycle}
                                onChange={(e) => setCreateUserData({ ...createUserData, billingCycle: e.target.value })}
                                options={[
                                    { value: 'monthly', label: 'Mensuel' },
                                    { value: 'yearly', label: 'Annuel' }
                                ]}
                            />
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                    <Button
                        variant="outline"
                        onClick={handleCreateModalClose}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleCreateUser}
                    >
                        Créer l'utilisateur
                    </Button>
                </div>
            </div>
        </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Supprimer l'utilisateur"
        message={`Êtes-vous sûr de vouloir supprimer ${userToDelete?.firstname} ${userToDelete?.lastname} ? Cette action est irréversible.`}
        confirmText="Supprimer"
        confirmVariant="danger"
      />
    </div>
  );
};

export default Users;
